import React, { useState, useCallback } from 'react';

import { TextField, TextFieldProps } from '@occc-app/design-system';
import { parseValueToOnlyDigits } from '@occc/utils';
import { Cpg } from '@occc/graphql';

import { ChangeSubmitterIdModal } from './change-submitter-id.modal';

import { ScanDataSubmissionGroupsUpdateFormValuesPaths } from '../scan-data-submission-groups-update.types';
import { tobaccoCompanyOptions } from '../scan-data-submission-groups-update.constants';

const onlyDigitsFieldFieldProps: TextFieldProps['fieldProps'] = {
  parse: parseValueToOnlyDigits,
};

const getCpgLabel = (cpg: Cpg): string => {
  const option = tobaccoCompanyOptions.find((opt) => opt.value === cpg);
  return option?.label || cpg;
};

export type SubmitterIdWithConfirmationProps = {
  cpg: Cpg;
  cpgWasChanged: boolean;
  submitterIdWasChanged: boolean;
};

export const SubmitterIdWithConfirmation = ({
  cpg,
  cpgWasChanged,
  submitterIdWasChanged,
}: SubmitterIdWithConfirmationProps) => {
  const [showModal, setShowModal] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const skipConfirmation = cpgWasChanged || submitterIdWasChanged || hasConfirmed;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (skipConfirmation) {
        return;
      }

      e.preventDefault();
      setShowModal(true);
    },
    [skipConfirmation],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      if (skipConfirmation) {
        return;
      }

      (e.target as HTMLInputElement).blur();
      setShowModal(true);
    },
    [skipConfirmation],
  );

  const handleConfirm = useCallback(() => {
    setHasConfirmed(true);
    setShowModal(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <TextField
        name={'sftpDetails.submitterID' satisfies ScanDataSubmissionGroupsUpdateFormValuesPaths}
        label='Submitter ID'
        margin='none'
        inputRef={inputRef}
        inputProps={
          skipConfirmation ? undefined : { onMouseDown: handleMouseDown, onFocus: handleFocus }
        }
        fieldProps={onlyDigitsFieldFieldProps}
      />
      <ChangeSubmitterIdModal
        show={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        cpgName={getCpgLabel(cpg)}
      />
    </>
  );
};
